/**
 * TicketTypeSelector Component
 *
 * Two-button toggle for selecting between Incident and Request ticket types.
 * Provides visual feedback and helper text for each option.
 */

import { TicketType } from '../../types/ticket';
import './TicketTypeSelector.css';

interface TicketTypeSelectorProps {
  value: TicketType | '';
  onChange: (type: TicketType) => void;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
}

export const TicketTypeSelector: React.FC<TicketTypeSelectorProps> = ({
  value,
  onChange,
  error,
  errorText,
  disabled = false,
}) => {
  const handleSelect = (type: TicketType) => {
    if (!disabled) {
      onChange(type);
    }
  };

  return (
    <div className={`ticket-type-selector ${error ? 'ticket-type-selector--error' : ''}`}>
      <label className="ticket-type-selector__label">
        Ticket Type
        <span className="ticket-type-selector__required">*</span>
      </label>

      <div className="ticket-type-selector__buttons">
        <button
          type="button"
          className={`ticket-type-button ticket-type-button--incident ${
            value === TicketType.INCIDENT ? 'ticket-type-button--selected' : ''
          }`}
          onClick={() => handleSelect(TicketType.INCIDENT)}
          disabled={disabled}
          aria-pressed={value === TicketType.INCIDENT}
        >
          <div className="ticket-type-button__icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 9v4M12 17h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="ticket-type-button__content">
            <span className="ticket-type-button__title">Incident</span>
            <span className="ticket-type-button__description">Something is broken or not working</span>
          </div>
          {value === TicketType.INCIDENT && (
            <div className="ticket-type-button__check">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13.3337 4L6.00033 11.3333L2.66699 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </button>

        <button
          type="button"
          className={`ticket-type-button ticket-type-button--request ${
            value === TicketType.REQUEST ? 'ticket-type-button--selected' : ''
          }`}
          onClick={() => handleSelect(TicketType.REQUEST)}
          disabled={disabled}
          aria-pressed={value === TicketType.REQUEST}
        >
          <div className="ticket-type-button__icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 5v14M5 12h14"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="ticket-type-button__content">
            <span className="ticket-type-button__title">Request</span>
            <span className="ticket-type-button__description">I need something new</span>
          </div>
          {value === TicketType.REQUEST && (
            <div className="ticket-type-button__check">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13.3337 4L6.00033 11.3333L2.66699 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </button>
      </div>

      {errorText && <span className="ticket-type-selector__error">{errorText}</span>}
    </div>
  );
};
